import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Slider,
    InputAdornment,
} from '@mui/material';
import { Polygon, GlobalContext } from '../../../context/context';
import { handlePolygonStyleEdit } from '../handlers/polygonStyleHandlers';

interface PolygonStyleModalProps {
    open: boolean;
    onClose: () => void;
    polygon: Polygon | null;
    global: GlobalContext;
    editLayer: (updatedLayer: any, layerId: number) => void;
    setSelectedPolygon: (polygon: Polygon | null) => void;
    setIsPolygonModalOpen: (open: boolean) => void;
}

const colorPalette = [
    '#FF0000', // Red
    '#FF4500', // Orange Red
    '#FFA500', // Orange
    '#FFD700', // Gold
    '#FFFF00', // Yellow
    '#32CD32', // Lime Green
    '#008000', // Green
    '#00CED1', // Dark Turquoise
    '#0000FF', // Blue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#FF1493', // Deep Pink
    '#A52A2A', // Brown
    '#000000', // Black
    '#808080', // Gray
    '#FFFFFF', // White
];

export default function PolygonStyleModal({ 
    open, 
    onClose, 
    polygon,
    global,
    editLayer,
    setSelectedPolygon,
    setIsPolygonModalOpen
}: PolygonStyleModalProps) {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const [editedPolygon, setEditedPolygon] = React.useState<Polygon | null>(null);

    React.useEffect(() => {
        if (polygon) {
            setEditedPolygon({
                ...polygon,
            });
        }
    }, [polygon]);

    if (!editedPolygon) return null;

    const handleSave = () => {
        console.log('handleSave called with editedPolygon:', editedPolygon);
        if (editedPolygon) {
            console.log('Calling handlePolygonStyleEdit...');
            handlePolygonStyleEdit({
                polygon: editedPolygon,
                global,
                editLayer,
                setSelectedPolygon,
                setIsPolygonModalOpen
            });
        } else {
            console.error('editedPolygon is null or undefined');
        }
    };

    const handleOpacityChange = (newOpacity: number) => {
        if (newOpacity >= 0 && newOpacity <= 1) {
            setEditedPolygon({ ...editedPolygon, opacity: newOpacity.toString() });
        }
    };

    const handleBorderWidthChange = (newWidth: number) => {
        if (newWidth >= 0 && newWidth <= 10) {
            setEditedPolygon({ ...editedPolygon, borderWidth: newWidth });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('mapDialogs:polygonStyle.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        label={t('mapDialogs:polygonStyle.polygonName')}
                        value={editedPolygon.name}
                        onChange={(e) => setEditedPolygon({ ...editedPolygon, name: e.target.value })}
                        fullWidth
                    />

                    <TextField
                        label={t('mapDialogs:polygonStyle.description')}
                        value={editedPolygon.description}
                        onChange={(e) => setEditedPolygon({ ...editedPolygon, description: e.target.value })}
                        fullWidth
                        multiline
                        rows={2}
                    />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:polygonStyle.fillColor')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {colorPalette.map((color) => (
                                <Box
                                    key={color}
                                    onClick={() => setEditedPolygon({ ...editedPolygon, colour: color })}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: color,
                                        border: editedPolygon.colour === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom>
                                {t('mapDialogs:polygonStyle.customFillColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: editedPolygon.colour,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={editedPolygon.colour}
                                    onChange={(e) => setEditedPolygon({ ...editedPolygon, colour: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:polygonStyle.borderColor')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {colorPalette.map((color) => (
                                <Box
                                    key={color}
                                    onClick={() => setEditedPolygon({ ...editedPolygon, borderColour: color })}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: color,
                                        border: editedPolygon.borderColour === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom>
                                {t('mapDialogs:polygonStyle.customBorderColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: editedPolygon.borderColour,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={editedPolygon.borderColour}
                                    onChange={(e) => setEditedPolygon({ ...editedPolygon, borderColour: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:polygonStyle.opacity')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={parseFloat(editedPolygon.opacity)}
                                onChange={(_, value) => handleOpacityChange(value as number)}
                                min={0}
                                max={1}
                                step={0.1}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                value={parseFloat(editedPolygon.opacity)}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                        handleOpacityChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                sx={{ width: 100 }}
                                size="small"
                            />
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:polygonStyle.borderWidth')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={editedPolygon.borderWidth}
                                onChange={(_, value) => handleBorderWidthChange(value as number)}
                                min={0}
                                max={10}
                                step={1}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                value={editedPolygon.borderWidth}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        handleBorderWidthChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                }}
                                sx={{ width: 100 }}
                                size="small"
                            />
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {t('mapDialogs:common.saveChanges')}
                </Button>
            </DialogActions>
        </Dialog>
    );
} 